/**
 * BatchDownloadButton Component
 *
 * Provides a button to download all QR codes for a site as a ZIP file.
 * Uses the batch generator to create QR codes for all bays and packages
 * them into a downloadable ZIP file.
 *
 * Requirements:
 * - 8.1: Provide a "Download All" button to generate and download all QR codes for the site
 * - 8.4: Package all generated QR codes into a single ZIP file
 * - 8.5: Name the ZIP file using the pattern: {site-name}-qr-codes.zip
 * - 8.6: Show a progress indicator during batch generation
 */

import { useState, useCallback } from 'react';
import JSZip from 'jszip';
import type { QrTemplate } from '../types';
import type { AdvancedOptions } from '../types/advancedOptions';
import { DEFAULT_ADVANCED_OPTIONS } from '../types/advancedOptions';
import { generateBatchQrCodes } from '../utils/batchGenerator';
import { generateZipFilename } from '../utils/filenameGenerator';
import { useSite } from '../../site/context/useSite';
import { useAuth } from '../../auth/useAuth';
import { useAnalytics } from '../../analytics/useAnalytics';
import type { QrBatchDownloadAttributes } from '../../analytics/trackingTypes';
import './BatchDownloadButton.css';

/**
 * Props for the BatchDownloadButton component
 */
export interface BatchDownloadButtonProps {
  /** The QR template with customization settings */
  readonly template: QrTemplate;
  /** The site identifier */
  readonly siteId: string;
  /** The site name for filename generation */
  readonly siteName: string;
  /** Array of fixed bays with bayId and bayNumber */
  readonly fixedBays: readonly { bayId: string; bayNumber: string }[];
  /** Array of super bays with bayId and bayName */
  readonly superBays: readonly { bayId: string; bayName: string }[];
  /** Whether the button should be disabled */
  readonly disabled?: boolean;
  /** Advanced options for URL generation */
  readonly advancedOptions?: AdvancedOptions;
  /** Tooltip text describing what will be downloaded */
  readonly tooltip?: string;
}

/**
 * Batch generation status
 */
type BatchStatus = 'idle' | 'generating' | 'zipping' | 'complete' | 'error';

/**
 * Progress state for batch generation
 */
interface BatchProgress {
  readonly status: BatchStatus;
  readonly completed: number;
  readonly total: number;
  readonly failedCount: number;
  readonly errorMessage: string | null;
}

/**
 * Initial progress state
 */
const INITIAL_PROGRESS: BatchProgress = {
  status: 'idle',
  completed: 0,
  total: 0,
  failedCount: 0,
  errorMessage: null,
};

/**
 * Download icon SVG component
 */
function DownloadAllIcon(): JSX.Element {
  return (
    <svg
      className="batch-download-button__icon"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <polyline
        points="7 10 12 15 17 10"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <line
        x1="12"
        y1="15"
        x2="12"
        y2="3"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Additional lines to indicate "all" */}
      <line
        x1="6"
        y1="6"
        x2="6"
        y2="3"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        opacity="0.6"
      />
      <line
        x1="18"
        y1="6"
        x2="18"
        y2="3"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        opacity="0.6"
      />
    </svg>
  );
}

/**
 * Spinner icon for loading state
 */
function SpinnerIcon(): JSX.Element {
  return (
    <svg
      className="batch-download-button__spinner"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <circle
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        opacity="0.25"
      />
      <path
        d="M12 2a10 10 0 0 1 10 10"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

/**
 * Converts a data URL to a Blob
 *
 * @param dataUrl - The data URL to convert
 * @returns The blob representation
 */
function dataUrlToBlob(dataUrl: string): Blob {
  const parts = dataUrl.split(',');
  const mimeMatch = parts[0].match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : 'image/png';
  const bstr = atob(parts[1]);
  const n = bstr.length;
  const u8arr = new Uint8Array(n);
  
  for (let i = 0; i < n; i++) {
    u8arr[i] = bstr.charCodeAt(i);
  }
  
  return new Blob([u8arr], { type: mime });
}

/**
 * Triggers a browser download for the given blob
 *
 * @param blob - The blob to download
 * @param filename - The filename for the download
 */
function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;

  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);

  URL.revokeObjectURL(url);
}

/**
 * Calculates the progress percentage
 *
 * @param completed - Number of completed items
 * @param total - Total number of items
 * @returns Progress percentage (0-100)
 */
function calculateProgressPercentage(completed: number, total: number): number {
  if (total === 0) return 0;
  return Math.round((completed / total) * 100);
}

/**
 * Gets the button text based on current status
 *
 * @param progress - Current progress state
 * @returns Button text to display
 */
function getButtonText(progress: BatchProgress): string {
  switch (progress.status) {
    case 'generating':
      return `Generating... ${calculateProgressPercentage(progress.completed, progress.total)}%`;
    case 'zipping':
      return 'Creating ZIP...';
    case 'complete':
      return 'Download Complete';
    case 'error':
      return 'Download Failed';
    default:
      return 'Download All';
  }
}

/**
 * BatchDownloadButton - Batch QR code download button
 *
 * Provides a button to download all QR codes for a site as a ZIP file.
 * Shows progress during generation and handles errors gracefully.
 *
 * @example
 * ```tsx
 * <BatchDownloadButton
 *   template={qrTemplate}
 *   siteId="site-123"
 *   siteName="My Golf Site"
 *   fixedBays={[{ bayId: 'bay-1', bayNumber: 1 }]}
 *   superBays={[{ bayId: 'super-1', bayName: 'VIP Area' }]}
 * />
 * ```
 */
export function BatchDownloadButton({
  template,
  siteId,
  siteName,
  fixedBays,
  superBays,
  disabled = false,
  advancedOptions,
  tooltip,
}: BatchDownloadButtonProps): JSX.Element {
  const [progress, setProgress] = useState<BatchProgress>(INITIAL_PROGRESS);
  const { ensureKey, isKeysLoading } = useSite();
  const { user } = useAuth();
  const { track } = useAnalytics();

  // Use default advanced options if not provided
  const effectiveAdvancedOptions = advancedOptions ?? DEFAULT_ADVANCED_OPTIONS;

  // Get author from user profile (name or email)
  const author = user?.profile?.name ?? user?.profile?.email ?? 'Unknown';

  const handleClick = useCallback(async () => {
    // Don't start if already in progress
    if (progress.status === 'generating' || progress.status === 'zipping') {
      return;
    }

    // Calculate total count (site QR + fixed bays + super bays)
    const total = 1 + fixedBays.length + superBays.length;

    // Reset and start generating
    setProgress({
      status: 'generating',
      completed: 0,
      total,
      failedCount: 0,
      errorMessage: null,
    });

    // Track batch download event with template attributes (Requirements 8.1, 8.2)
    // Note: Center-related properties are only meaningful when centerEnabled is true,
    // so we report null for these when center customization is disabled
    const trackingAttributes: QrBatchDownloadAttributes = {
      SiteId: siteId,
      BayCount: total,
      Color: template.color,
      CenterEnabled: template.centerEnabled,
      ShowCornerLabel: template.showCornerLabel,
      BottomLogo: template.bottomLogo,
      // Center-related properties - null when centerEnabled is false
      ShowBayLabel: template.centerEnabled ? template.showBayLabel : null,
      FontFamily: template.centerEnabled ? template.fontFamily : null,
      FontSize: template.centerEnabled ? template.fontSize : null,
      HasLogo: template.centerEnabled ? template.logoDataUrl !== null : null,
      CenterPadding: template.centerEnabled ? template.centerPadding : null,
      BayLabelColor: template.centerEnabled ? template.bayLabelColor : null,
      AdvancedEnabled: effectiveAdvancedOptions.enabled,
      ExperimentalFeatures: effectiveAdvancedOptions.experimentalFeatures,
      LanguageOverride: effectiveAdvancedOptions.languageOverride,
      SwingCapture: effectiveAdvancedOptions.swingCapture,
      GameMode: effectiveAdvancedOptions.gameMode,
    };
    track('Pocket Admin QR Batch Download', trackingAttributes);

    try {
      // Ensure we have a site key before generating URLs (Requirement 3.3)
      const siteKey = await ensureKey();

      // Generate all QR codes with progress tracking
      const result = await generateBatchQrCodes(
        template,
        siteId,
        siteName,
        fixedBays,
        superBays,
        siteKey,
        (completed, totalCount) => {
          setProgress((prev) => ({
            ...prev,
            completed,
            total: totalCount,
          }));
        },
        effectiveAdvancedOptions,
        author
      );

      // Update status to zipping
      setProgress((prev) => ({
        ...prev,
        status: 'zipping',
        failedCount: result.failed.length,
      }));

      // Create ZIP file
      const zip = new JSZip();

      for (const qrResult of result.successful) {
        const blob = dataUrlToBlob(qrResult.dataUrl);
        zip.file(qrResult.filename, blob);
      }

      // Generate the ZIP blob
      const zipBlob = await zip.generateAsync({ type: 'blob' });

      // Generate filename and trigger download
      const zipFilename = generateZipFilename(siteName);
      triggerDownload(zipBlob, zipFilename);

      // Update to complete status
      setProgress((prev) => ({
        ...prev,
        status: 'complete',
        failedCount: result.failed.length,
      }));

      // Reset to idle after a short delay
      setTimeout(() => {
        setProgress(INITIAL_PROGRESS);
      }, 2000);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'An unexpected error occurred';
      
      setProgress((prev) => ({
        ...prev,
        status: 'error',
        errorMessage,
      }));

      // Reset to idle after showing error
      setTimeout(() => {
        setProgress(INITIAL_PROGRESS);
      }, 3000);
    }
  }, [template, siteId, siteName, fixedBays, superBays, progress.status, ensureKey, effectiveAdvancedOptions, author, track]);

  // Determine if button should be disabled
  const isGenerating = progress.status === 'generating' || progress.status === 'zipping';
  const isDisabled = disabled || isGenerating || isKeysLoading;

  // Calculate progress percentage for the progress bar
  const progressPercentage = calculateProgressPercentage(progress.completed, progress.total);

  return (
    <div className="batch-download-button-container">
      <button
        type="button"
        className={`batch-download-button ${isGenerating ? 'batch-download-button--generating' : ''} ${progress.status === 'error' ? 'batch-download-button--error' : ''} ${progress.status === 'complete' ? 'batch-download-button--complete' : ''}`}
        onClick={handleClick}
        disabled={isDisabled}
        aria-label="Download all QR codes as ZIP file"
        aria-busy={isGenerating}
        title={tooltip}
      >
        {isGenerating ? <SpinnerIcon /> : <DownloadAllIcon />}
        <span className="batch-download-button__text">{getButtonText(progress)}</span>
      </button>

      {/* Tooltip display on hover */}
      {tooltip && (
        <span className="batch-download-button__tooltip">{tooltip}</span>
      )}

      {/* Progress bar - Requirement 8.6 */}
      {isGenerating && (
        <div
          className="batch-download-button__progress-bar"
          role="progressbar"
          aria-valuenow={progressPercentage}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`Batch generation progress: ${progressPercentage}%`}
        >
          <div
            className="batch-download-button__progress-fill"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
      )}

      {/* Error message display */}
      {progress.status === 'error' && progress.errorMessage && (
        <div className="batch-download-button__error" role="alert">
          {progress.errorMessage}
        </div>
      )}

      {/* Partial success message */}
      {progress.status === 'complete' && progress.failedCount > 0 && (
        <div className="batch-download-button__warning" role="status">
          {progress.failedCount} QR code{progress.failedCount > 1 ? 's' : ''} failed to generate
        </div>
      )}
    </div>
  );
}
